import React from 'react';
import { Typography, Card, Button } from 'antd';
import styles from './License.module.scss';
import { useSelector, useDispatch } from 'react-redux';
import { AppState } from 'store';
import { usernameSelector } from 'common';
import { setLicenseFlag } from './License.actions';
import { Redirect } from 'react-router-dom';
import { useAccount, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { getAccessToken } from 'common/helpers';

const License: React.FC = () => {
  const dispatch = useDispatch();
  const login = useSelector((state: AppState) => state.login);
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});

  const { username } = useSelector((state: AppState) => ({
    username: usernameSelector(state),
  }));

  const handleSave = (format: string) => {
    if (account && inProgress === InteractionStatus.None) {
      getAccessToken(instance, account, inProgress).then((response) => {
        if (response?.accessToken)
          dispatch(setLicenseFlag(username, response.accessToken));
      });
    }
  };

  if (login.user?.licenseflag == 'True') {
    return <Redirect to={`/pages/dashboard`} />;
  }

  return (
    <Card>
      <Typography>
        <table>
          <tbody>
            <tr>
              <td className={styles.bold} align="center">
                END USER LICENSE AGREEMENT
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  This End User License Agreement ("Agreement") accompanies the
                  information, data, images, reports, and/or software (the
                  "Services") that you are accessing. By accessing the Services,
                  you are agreeing to all the terms and conditions of this
                  agreement, including the product, pricing and billing
                  information in any account confirmation containing account
                  information received at any time ("Account Confirmation"), the
                  terms of which are incorporated into this Agreement by
                  reference. If, however, you have an operative master services
                  agreement (with applicable statements of work), or a
                  standalone services agreement covering use of the Services,
                  the terms of that services agreement, not this Agreement,
                  shall control with regard to the Services. Data Trace
                  Information Services LLC, a Delaware limited liability
                  company, and (when applicable to the particular Service being
                  provided), its affiliate companies (collectively, "Data
                  Trace"), grants you ("Customer") a limited, non-exclusive,
                  non-transferable license to use the Services, provided you
                  accept the following terms and conditions:
                </p>
                <p>
                  <b>1. Property.</b> The Services and all intellectual property
                  rights therein are owned by Data Trace. No ownership rights
                  are granted by this Agreement and, except for the limited
                  license provided, Data Trace reserves all rights in and to the
                  Services and all underlying data compilations and information
                  contained therein, including but not limited to the exclusive
                  intellectual property rights and the right to grant further
                  licenses. Customer acknowledges that the Services are the
                  proprietary property of Data Trace and are a valuable
                  commercial product, the development of which involved an
                  expenditure of substantial time and money by Data Trace.{' '}
                </p>
                <p>
                  <b>2. Permitted Use.</b> The Services are solely for use
                  within Customer's own organization by Customer's own employees
                  for Customer’s own internal business purposes of verifying
                  property ownership and lien information. Customer shall not
                  resell, relicense or redistribute the Services in whole or in
                  part except that Customer may resell and redistribute
                  individual (not bulk) images and/or Online Services in the
                  ordinary course of their business.
                </p>
                <p>
                  <b>3. Restrictions on Use.</b> Both during and after the term
                  of this Agreement, Customer agrees as follows:{' '}
                </p>
                <p>
                  (a) Customer shall not (i) disclose, use, disseminate,
                  reproduce or publish any portion of the Services in any manner
                  other than as expressly permitted in this Agreement, (ii)
                  permit any parent, subsidiary, other affiliated entity or
                  other third party, including any third party entity involved
                  in a joint marketing arrangement with Customer, to use the
                  Services or any portion thereof, (iii) resell, relicense or
                  redistribute the Services in whole or in part (iv) use the
                  Services to create any derivative products, (v) use the
                  Services to create, enhance or structure any database in any
                  form for resale or distribution, (vi) grant access to the
                  Services, or any portion thereof, to individuals incarcerated
                  in prisons or correctional institutions, (vii) allow access to
                  the Services through any terminal located outside of the
                  United States or outside of Customer’s operations without
                  prior written consent of Data Trace, (viii) use the Services
                  outside the United States without prior written consent of
                  Data Trace.
                </p>
                <p>
                  (b) Customer shall (i) comply with the published guidelines of
                  the Direct Marketing Association, other applicable industry
                  guidelines, and all federal, state, and local laws,
                  regulations, ordinances and court orders from competent
                  jurisdictions regarding the use, storage and dissemination of
                  data such as the Services, and (ii) abide by all prevailing
                  federal, state, and local laws, regulations, ordinances and
                  court orders from competent jurisdictions, including but not
                  limited to those governing non-solicitation, privacy, data
                  protection and security. Costumer understands and acknowledges
                  that Customer’s use of the Services is also subject to the
                  terms and conditions of Data Trace’s Privacy Policy, which is
                  available on Data Trace’s affiliate website located at{' '}
                  <a
                    href="https://www.firstam.com/privacy-policy/index.html"
                    target="_blank"
                    rel="noreferrer"
                  >
                    https://www.firstam.com/privacy-policy/index.html
                  </a>
                  . For the avoidance of doubt, Customer hereby consents to the
                  collection and storage of information by Data Trace related to
                  the Services and use by Data Trace of statistical and
                  aggregated data concerning the use of the Services. Customer,
                  in using the Services, understands and accepts that it has no
                  expectation of privacy other than as may be set forth herein.
                </p>
                <p>
                  (c) Customer understands that the information, data, images,
                  and reports available under the Services have not been
                  collected for credit purposes and are not intended to be
                  indicative of any consumer’s credit worthiness, credit
                  standing, credit capacity, or other characteristics listed in
                  Section 1681(a) of the Fair Credit Reporting Act, 15 U.S.C. §
                  1681 et seq. Customer shall not use the Services (i) as a
                  factor in establishing an individual’s eligibility for credit
                  or insurance, (ii) in connection with underwriting individual
                  insurance, (iii) in evaluating an individual for employment
                  purposes, (iv) in connection with a determination of an
                  individual’s eligibility for a license or other benefit
                  granted by a governmental authority, (v) in any way that would
                  cause the Services to constitute a “consumer report” under the
                  Fair Credit Reporting Act, 15 U.S.C. § 1681 et seq., or (vi)
                  in any other manner that would cause such use of the Services
                  to be construed as a consumer report by any pertinent
                  governmental authority.{' '}
                </p>
                <p>
                  (d) Customer shall be solely responsible for maintaining the
                  confidentiality of any usernames and passwords assigned to
                  users of Customer’s account(s), and Customer shall be
                  responsible for all use of the Services (including all
                  corresponding fees) attributable to said usernames and
                  passwords, whether or not authorized by Customer.
                </p>
                <p>
                  (e) Customer shall not use the Services for any purpose that
                  (i) infringes any third party's copyright, patent, trademark,
                  trade secret or other proprietary rights or rights of
                  publicity or privacy, or (ii) is defamatory, trade libelous,
                  unlawfully threatening or unlawfully harassing.
                </p>
                <p>
                  (f) Customer shall not remove, alter or obscure any
                  proprietary notices in the Services and will reproduce all
                  such notices on all copies or portions thereof.
                </p>
                <p>
                  <b>4. Delivery of Data and Format.</b> Customer acknowledges
                  that the availability of data elements and images in the
                  Services varies substantially from area-to-area, and
                  circumstances may exist or arise which prevent Data Trace from
                  providing such data or achieving complete representation of
                  all data elements or images in the Services. Notwithstanding
                  anything to the contrary, Data Trace may limit or discontinue
                  the provision of the Services for geographic locations where:
                  (i) Data Trace is restricted by rules, regulations, laws or
                  governmental entities; (ii) Data Trace has discontinued the
                  collection of data and/or images; or (iii) Data Trace is
                  prohibited by third party providers. Data Trace may
                  discontinue production, support, and maintenance of any
                  Services if Data Trace develops an upgraded version or
                  otherwise can no longer provide such Services.
                </p>
                <p>
                  <b>5. Compliance Audits.</b> Data Trace reserves the right,
                  during normal business hours, on reasonable notice, and at
                  Data Trace’s expense, to audit the Customer to ensure
                  Customer's compliance with the terms and conditions of this
                  Agreement. Data Trace shall select an auditor in its sole
                  discretion. If such auditor determines there has been a breach
                  in Customer's compliance with the terms of this Agreement,
                  Data Trace may immediately terminate this Agreement and pursue
                  its other legal remedies. Should Customer not cooperate with
                  Data Trace’s audit request within five (5) days, Customer
                  shall be deemed to have conclusively admitted to a material
                  breach in Customer's compliance for which Data Trace may
                  immediately terminate this Agreement and pursue its legal
                  remedies.
                </p>
                <p>
                  <b>6. Fees. </b> In consideration of the rights granted to
                  Customer hereunder, Customer shall pay to Data Trace the fees
                  stated within the Account Confirmation. By submitting an order
                  to Data Trace, Customer authorizes Data Trace to charge the
                  credit card entered by Customer for the Services at the fees
                  stated within the Account Confirmation. With respect to
                  prepaid accounts, Customer acknowledges and agrees that: (a)
                  the fees stated within the Account Confirmation will be
                  deducted from the amount Customer previously paid to and
                  placed on deposit with Data Trace (the “Prepaid Plan
                  Balance”); (b) Data Trace may, from time-to-time during the
                  term, apply all or a part of the Prepaid Plan Balance toward
                  such payment of fees without notice to or further
                  authorization from Customer; (c) Data Trace may, from
                  time-to-time during the term, require Customer to replenish
                  the Prepaid Plan Balance to the dollar amount stated within
                  the Account Confirmation; (d) any failure or refusal on the
                  part of Customer to promptly replenish the Prepaid Plan
                  Balance as requested by Data Trace may result in the
                  suspension of Services and/or the termination of this
                  Agreement, in Data Trace’s sole and absolute discretion; (e)
                  in the event Customer, for any reason or cause (regardless of
                  fault), continues to access and use the Services after the
                  expiration or other termination of this Agreement or incurs
                  fees in an amount that exceeds the Prepaid Plan Balance then
                  on deposit with Data Trace, Customer shall be obligated for,
                  and shall immediately pay to Data Trace the fees for such
                  access and use of the Services at the rates set forth in the
                  Account Confirmation; (f) Data Trace shall not be required to
                  keep the Prepaid Plan Balance separate from its general funds
                  and Customer shall not be entitled to interest on the Prepaid
                  Plan Balance; and (g) all funds paid to Data Trace as and for
                  a contribution to or replenishment of the Prepaid Plan Balance
                  are not refundable and any credit balance remaining at the end
                  of each term shall be forfeited. Fees are exclusive of use, ad
                  valorem, personal property, and other taxes, which are the
                  responsibility of Customer. Data Trace shall charge Customer
                  applicable sales tax, and Customer shall be responsible for
                  filing all other taxes. Data Trace reserves the right to
                  change the fees for the Services at any time. Additional
                  charges may apply for training users at Customer locations.
                  Customer shall provide all Internet connectivity, hardware and
                  software necessary to access the Services.
                </p>
                <p>
                  <b>7. Term and Termination.</b> The initial term of this
                  Agreement is twelve (12) months commencing on the date
                  Customer enters into this Agreement. Thereafter, the term
                  shall automatically renew for additional successive twelve
                  (12) month terms, unless terminated by Customer giving the
                  Data Trace not less than thirty (30) calendar days written
                  notice of termination prior to the expiration of the
                  then-current term. Data Trace may terminate this Agreement at
                  any time in its sole discretion with or without notice to
                  Customer. If Data Trace breaches any provision of this
                  Agreement, Customer shall, upon providing written notice of
                  such breach, be entitled to immediately terminate this
                  Agreement, provided such breach is not cured within thirty
                  (30) days following such notice. Upon termination of this
                  Agreement by either party, Customer, at its own expense, shall
                  return all Services to Data Trace or certify that the Services
                  have been destroyed within ten (10) business days of
                  termination, and any amounts unpaid by Customer shall be
                  immediately due and payable. Failure to return or certify the
                  destruction of the Services to Data Trace in accordance with
                  this section will result in: (i) Customer’s obligation to pay
                  a perpetual license fee for the Services; or (ii) Customer’s
                  obligation to permit Data Trace's agent to have access to
                  Customer's premises for the retrieval of the Services and
                  Customer shall pay the actual costs as reasonably incurred by
                  Data Trace to retrieve same.
                </p>
                <p>
                  <b>8. General Disclaimer.</b> THE SERVICES ARE INFORMATIONAL
                  ONLY AND ARE NOT INTENDED TO PROVIDE SPECIFIC COMMERCIAL,
                  FINANCIAL OR INVESTMENT ADVICE. THE SERVICES ARE BASED UPON
                  CERTAIN DATA AND/OR RECORDINGS, SUBJECT TO FREQUENT CHANGE.
                  DATA TRACE MAKES NO WARRANTIES OR REPRESENTATIONS ABOUT THE
                  METHODOLOGIES USED OR THE ACCURACY, TIMELINESS, RELIABILITY OR
                  COMPLETENESS OF ANY OF THE SERVICES. THE SERVICES ARE PROVIDED
                  ON AN “AS IS” BASIS, WITHOUT WARRANTIES OF ANY KIND
                  WHATSOEVER, INCLUDING ANY IMPLIED OR EXPRESS WARRANTIES OF
                  MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE OR
                  NON-INFRINGEMENT. DATA TRACE DOES NOT REPRESENT OR WARRANT
                  THAT THE SERVICES WILL BE AVAILABLE 24 HOURS PER DAY, SEVEN
                  DAYS PER WEEK. ANY RELIANCE ON OR USE BY CUSTOMER OF THE
                  SERVICES SHALL BE ENTIRELY AT CUSTOMER’S OWN RISK. DATA TRACE
                  MAKES NO REPRESENTATIONS OR WARRANTIES ABOUT THE LEGALITY OR
                  PROPRIETY OF THE USE OF THE SERVICES IN ANY JURISDICTION,
                  STATE OR REGION. CUSTOMER SHALL BE SOLELY RESPONSIBLE FOR
                  OBTAINING ANY AND ALL NECESSARY LICENSES, CERTIFICATES,
                  PERMITS, APPROVALS OR OTHER AUTHORIZATIONS REQUIRED BY
                  FEDERAL, STATE OR LOCAL STATUTE, LAW OR REGULATION APPLICABLE
                  TO CUSTOMER’S USE OF THE SERVICES. THE SERVICES ARE NOT TO BE
                  CONSTRUED AS A SUFFICIENT BASIS FOR UNDERWRITING TITLE
                  INSURANCE POLICIES AND NOTHING IN THIS AGREEMENT SUPERCEDES,
                  AMENDS, OR IN ANY WAY EXCUSES CUSTOMER’S TITLE INSURANCE
                  UNDERWRITING OBLIGATIONS IMPOSED BY ANY APPLICABLE TITLE
                  INSURANCE UNDERWRITING AGREEMENTS. THE SERVICES ARE
                  EXCLUSIVELY FOR CUSTOMER AND NOT FOR THE BENEFIT OF ANY THIRD
                  PARTIES.
                </p>
                <p>
                  <b>9. General Limitation of Liability.</b> DATA TRACE
                  DISCLAIMS ANY AND ALL LIABILITY TO ANY PERSON OR ENTITY FOR
                  THE PROPER PERFORMANCE OF SERVICES REFLECTING THE CONDITION OF
                  TITLE TO REAL PROPERTY. DATA TRACE SHALL HAVE NO LIABILITY
                  UNDER OR IN ANY WAY RELATED TO THIS AGREEMENT FOR ANY LOSS OF
                  PROFIT OR REVENUE OR FOR ANY CONSEQUENTIAL, INDIRECT,
                  INCIDENTAL, SPECIAL, PUNITIVE, OR EXEMPLARY DAMAGES, EVEN IF
                  DATA TRACE IS AWARE OF THE POSSIBILITY OF SUCH LOSS OR
                  DAMAGES. SOME STATES DO NOT ALLOW THE EXCLUSION OR LIMITATION
                  OF INCIDENTAL OR CONSEQUENTIAL DAMAGES, SO THE ABOVE
                  LIMITATION OR EXCLUSION MAY NOT APPLY. CUSTOMER AGREES THAT
                  THE LIMITATIONS SET FORTH ABOVE ARE FUNDAMENTAL ELEMENTS OF
                  THIS AGREEMENT, AND THAT THE SERVICES WOULD NOT BE PROVIDED TO
                  CUSTOMER ABSENT SUCH LIMITATIONS.
                </p>
                <p>
                  <b>10. Specific Disclaimers and Limitations of Liability.</b>{' '}
                  For the Services specified in this section, the specific
                  disclaimers and limitations of liability shall supplement the
                  general disclaimers and limitations of liability above. Unless
                  expressly stated below, all terms of the general disclaimers
                  and limitations of liability shall apply to all Services.
                </p>
                <p>
                  (a) Recording Services. If Data Trace incorrectly records any
                  document due to its own error, Customer's sole remedy shall
                  consist of Data Trace’s payment of recording fees associated
                  with Data Trace’s timely re-recording of the documents in the
                  public records. Data Trace will not be responsible for any
                  penalties and/or interest assessed, obtaining the original
                  document, or any newly executed document, or the re-execution
                  of the documents when necessary for re-recording. Data Trace
                  shall have no monetary liability to Customer other than to pay
                  the recording fees associated with the re-recording as stated
                  above. Data Trace further disclaims all liability for any
                  expenses, damages or penalties regarding misinformation, or
                  lack thereof, provided by Customer to Data Trace as to tax
                  exempt status of the mortgagee, or the incorrect preparation,
                  or lack of preparation, of forms related to mortgage/transfer
                  taxes.
                </p>
                <p>
                  All warranties set forth in the above paragraph are
                  non-transferable and the Parties expressly agree that there
                  are no third party beneficiaries of this warranty, intentional
                  or otherwise, including, but not limited to, any investors,
                  parties in the secondary market, or other parties to whom the
                  Customer may ultimately sell or transfer a loan or group of
                  loans.
                </p>
                <p>
                  <b>11. Indemnification. </b>CUSTOMER AGREES TO INDEMNIFY AND
                  HOLD DATA TRACE HARMLESS FROM AND AGAINST ALL CLAIMS OF THIRD
                  PARTIES ARISING OUT OF OR RELATED TO THE USE OF THE SERVICES
                  BY THE CUSTOMER, OR ATTRIBUTABLE TO CUSTOMER’S BREACH OF THIS
                  AGREEMENT; PROVIDED THAT DATA TRACE GIVES CUSTOMER PROMPT
                  WRITTEN NOTICE OF ANY SUCH CLAIM. DATA TRACE SHALL CONTROL THE
                  DEFENSE AND ANY SETTLEMENT OF SUCH CLAIM, AND CUSTOMER SHALL
                  COOPERATE WITH DATA TRACE IN DEFENDING AGAINST SUCH CLAIM.
                </p>
                <p>
                  <b>12. Copyrights.</b> Data Trace respects the intellectual
                  property rights of others and takes its obligations to comply
                  with intellectual property law seriously. If Customer believes
                  that a copyright owner’s work has been reproduced in the
                  Services in a copyright-infringing manner, Customer may notify
                  Data Trace and provide the following information:
                </p>
                <p>
                  (a) Identification of the work for which Customer claims the
                  copyright has been infringed;
                </p>
                <p>
                  (b) Identification of the material in the Services that
                  Customer claims is infringing and needs to be removed (with
                  sufficient specificity to enable Data Trace to locate said
                  material);
                </p>
                <p>
                  (c) Customer’s contact information, including address,
                  telephone number, and (if available) email address;
                </p>
                <p>
                  (d) A signed statement indicating that: (i) Customer has a
                  good faith belief that Data Trace’s use of the material is not
                  authorized by the copyright owner, and (ii) the information in
                  the notification is accurate, and that, under penalty of
                  perjury, Customer is the copyright owner or is authorized to
                  act on the copyright owner’s behalf.
                </p>
                <p>Notices of copyright infringement should be sent to:</p>
                <p>
                  Data Trace Information Services LLC
                  <br />
                  200 Commerce
                  <br />
                  Irvine, CA 92602
                  <br />
                  Attn: Legal Department <br />
                  Or by email to: DataTrace_Copyright@datatracetitle.com
                </p>
                <p>
                  <b>14. General.</b>
                </p>
                <p>
                  (a) Unless specified otherwise in a fully-executed license
                  agreement with Data Trace, this Agreement constitutes the
                  entire agreement between the parties with respect to the
                  Services and supersedes any prior understanding or agreement,
                  oral or written, relating to the Services.
                </p>
                <p>
                  (b) The interpretation and construction of this Agreement, and
                  all matters relating hereto, shall be governed by the laws of
                  the State of California applicable to agreements executed and
                  to be performed solely within such State. Any action or other
                  proceeding to enforce or interpret the terms of this Agreement
                  and/or the obligations, duties or rights of the parties
                  contained herein shall be brought before and resolved in the
                  State or U.S. District Court located within Orange County,
                  California, having subject matter jurisdiction over the issues
                  raised by such action or proceeding and the parties hereby
                  submit to the personal jurisdiction of said court for all such
                  purposes. This Agreement shall not be governed by the United
                  Nations Convention on Contracts for the International Sale of
                  Goods.{' '}
                </p>
                <p>
                  (c) The prevailing party shall be awarded its reasonable
                  attorney's fees and costs in any lawsuit arising out of or
                  related to this Agreement.
                </p>
                <p>
                  (d) No modification, amendment, supplement to or waiver of any
                  provision of this Agreement shall be effective unless in
                  writing and duly signed by an authorized representative of
                  both parties hereto.{' '}
                </p>
                <p>
                  (e) Any provision of this Agreement that contemplates
                  performance subsequent to the expiration or earlier
                  termination of this Agreement shall survive such expiration or
                  termination and shall continue in full force and effect until
                  fully satisfied.
                </p>
                <p>
                  (f) Data Trace shall not be liable for any delay or failure in
                  its performance of any of the acts required by this Agreement
                  when such delay or failure arises for reasons beyond Data
                  Trace’s reasonable control.
                </p>
                <p>
                  (g) Customer may not assign this Agreement or any rights or
                  obligations hereunder.
                </p>
                <p>
                  (h) Neither party shall use, or permit their respective
                  employees, agents and subcontractors to use the trademarks,
                  service marks, copyrighted material, logos, names, or any
                  other proprietary designations of the other party, or the
                  other party’s affiliates, whether registered or unregistered,
                  without such other party’s prior written consent.
                </p>
                <p>
                  (i) Except with Data Trace’s prior written approval, Customer
                  shall not disclose Data Trace as a data source to any third
                  party, unless required by federal, state or local laws or
                  government regulations and with prior notice to Data Trace.
                </p>
                <p>
                  (j) Customer shall provide for physical security of the
                  Services with the same degree of care (provided that such is
                  at least a reasonable degree of care) that Customer uses to
                  protect its own most sensitive data.
                </p>
                <p>
                  (k) Any notice or other communication required or permitted
                  under this Agreement shall be sufficiently given if delivered
                  in person or sent by one of the following methods: (a)
                  registered U.S. mail, return receipt requested (postage
                  prepaid); (2) certified U.S. mail, return receipt requested
                  (postage prepaid); or (3) commercially recognized overnight
                  service with tracking capabilities. Notices to Data Trace
                  shall be sent to 200 Commerce, Irvine, California 92602, with
                  a copy to Data Trace’s counsel at the same address marked
                  Attention: Legal Department. Notices to Customer shall be sent
                  to the address entered by Customer in the registration
                  information. Notices or communications shall be deemed
                  properly delivered as of the date personally delivered or sent
                  by mail or overnight service.
                </p>
                <p>
                  By accessing the information contained within the Services and
                  clicking “I Accept,” Customer agrees to be bound by all terms
                  and conditions contained in this Agreement. By clicking “I
                  Accept,” you assert that you are an authorized agent of
                  Customer with the authority to bind Customer to the terms and
                  conditions contained in this Agreement. If you do not accept
                  the terms and conditions contained herein, you may not use the
                  Services.
                </p>
              </td>
            </tr>
            <tr>
              <td align="center">
                <Button
                  type="primary"
                  htmlType="submit"
                  onClick={() => handleSave('accept')}
                  className={styles.button}
                >
                  I Accept
                </Button>
              </td>
            </tr>
          </tbody>
        </table>
      </Typography>
    </Card>
  );
};

export default License;
